// FeedContainer.jsx
import { useEffect, useState, useRef } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  where,
  getDocs,
  setDoc,
  doc,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../../modules/auth/firebaseConfig";

import FeedPost from "./FeedPost";
import CategoryFilter from "./CategoryFilter";

export default function FeedContainer() {
  const [posts, setPosts] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("All");

  const observerRef = useRef(null);
  const postsRef = collection(db, "posts");

  // -----------------------------------------------------
  // AUTOMATICALLY CREATE DEFAULT POST IF DATABASE IS EMPTY
  // -----------------------------------------------------
  const createDefaultPostIfEmpty = async () => {
    const snapshot = await getDocs(postsRef);

    if (snapshot.empty) {
      await setDoc(doc(postsRef), {
        mediaUrl:
          "https://firebasestorage.googleapis.com/v0/b/million2trillion.appspot.com/o/demo%2Fsample-video.mp4?alt=media",
        mediaType: "video",
        chartUrl:
          "https://firebasestorage.googleapis.com/v0/b/million2trillion.appspot.com/o/demo%2Fsample-chart.png?alt=media",
        caption: "EURUSD Trade: Key Levels",
        analysisPoints: [
          "Breakout above 1.0850",
          "Support at 1.0820",
          "MACD turning bullish",
        ],
        role: "Trader",
        category: "Forex",
        userId: "testuser@example.com",
        likes: 12,
        views: 130,
        timestamp: serverTimestamp(),
      });

      console.log("✨ Default demo post created automatically.");
    }
  };

  // ------------------------
  // Load FIRST batch of posts
  // ------------------------
  const loadPosts = async () => {
    setLoading(true);

    let q;

    if (category === "All") {
      q = query(postsRef, orderBy("timestamp", "desc"), limit(5));
    } else {
      q = query(
        postsRef,
        where("category", "==", category),
        orderBy("timestamp", "desc"),
        limit(5)
      );
    }

    onSnapshot(q, (snapshot) => {
      const fetched = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPosts(fetched);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      setLoading(false);
    });
  };

  // ------------------------
  // Load MORE posts (infinite scroll)
  // ------------------------
  const loadMorePosts = async () => {
    if (!lastDoc) return;

    let q;

    if (category === "All") {
      q = query(
        postsRef,
        orderBy("timestamp", "desc"),
        startAfter(lastDoc),
        limit(5)
      );
    } else {
      q = query(
        postsRef,
        where("category", "==", category),
        orderBy("timestamp", "desc"),
        startAfter(lastDoc),
        limit(5)
      );
    }

    onSnapshot(q, (snapshot) => {
      const fetched = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPosts((prev) => [...prev, ...fetched]);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
    });
  };

  // -------------------------
  // Observer for infinite scroll
  // -------------------------
  const lastPostRef = (node) => {
    if (loading) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMorePosts();
      }
    });

    if (node) observerRef.current.observe(node);
  };

  // -------------------------
  // INITIALIZE FEED
  // -------------------------
  useEffect(() => {
    createDefaultPostIfEmpty(); // auto demo post
    loadPosts(); // load feed
  }, [category]);

  // -------------------------
  // RETURN FEED UI
  // -------------------------
  return (
    <div className="w-full max-w-xl mx-auto">

      {/* CATEGORY FILTER BAR */}
      <CategoryFilter setCategory={setCategory} />

      {/* FEED POSTS */}
      {posts.map((post, index) => (
        <div
          key={post.id}
          ref={index === posts.length - 1 ? lastPostRef : null}
        >
          <FeedPost post={post} />
        </div>
      ))}

      {loading && (
        <p className="text-center text-gray-400 py-4">Loading...</p>
      )}
    </div>
  );
}
